import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import queryString from 'query-string';
import {
  actions, BookingSlot, BookingSlotsByDay, ExternalMeetingInfo, FetchReturn,
  MeetingQuestionAnswerSubmission, MeetingStatus, RecurrenceRule, NormalizedExternalParticipant,
  RootState, ThunkDispatchType
} from "../../store";
import { DateTime } from "luxon";
import { createMeetingSlotsFromRecurrenceRules, getLocalTimeZoneName } from "../../utils/scheduleUtils";
import BookMeeting, { MeetingBookingStep } from "./BookMeeting";
import { trackEvent } from "../../utils/appAnalyticsUtils";
import { EVENT_TYPE } from "../../constants";
import { successConfetti } from "../../utils/renderUtils";
import { CabButton } from "@CabComponents/CabButton";
import { CabModal } from "@CabComponents/CabModal";
import { useMountEffect } from "../../utils/hooks";
import { isMobile } from "../../utils/screenSizeUtils";
import { core } from "../../store/cabinetApi/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { scheduling } from "../../store/cabinetApi/scheduling";
import { formatRecurrenceRuleText } from "../../utils/shareTimeMessageFormatter";

const orderTimeSlots = (a: number, b: number) => a - b;

type RecurringBookingSlot = BookingSlot & {
  recurrenceSeriesKey?: string;
  recurrenceRule?: RecurrenceRule;
  recurrenceText?: string;
  recurrenceSeriesDates?: string[];
  recurrencePreviewDates?: string[];
};

const getSlotDurationMinutes = (slot: Pick<BookingSlot, 'start' | 'end'>) => (
  DateTime.fromISO(slot.end).diff(DateTime.fromISO(slot.start), 'minutes').minutes
);

const getRecurringBookingView = (
  meeting: ExternalMeetingInfo,
  bookingSlots: BookingSlot[],
  timezoneName: string,
): { selectableSlots: RecurringBookingSlot[]; visibleDates: DateTime[] } | null => {
  if (!meeting.is_recurring) return null;

  const selectableSlots = bookingSlots.flatMap(slot => {
    if (!slot.recurrence) return [];
    const slotDurationMinutes = getSlotDurationMinutes(slot);
    const slotRule: RecurrenceRule = {
      ...slot.recurrence,
      dtstart: slot.start,
      duration_minutes: slotDurationMinutes,
    };
    const slotStart = DateTime.fromISO(slot.start);
    const recurrencePreviewDates = createMeetingSlotsFromRecurrenceRules(
      [slotRule],
      slotStart.startOf('day'),
      slotStart.plus({ years: 10 }).endOf('day'),
      meeting.id,
      meeting.title || "Recurring Meeting",
    ).map(generatedSlot => DateTime.fromISO(generatedSlot.start_date).setZone(timezoneName).toISO() || "");

    return [{
      ...slot,
      recurrenceSeriesKey: slot.start,
      recurrenceRule: slotRule,
      recurrenceSeriesDates: [DateTime.fromISO(slot.start).setZone(timezoneName).toISO() || ""],
      recurrencePreviewDates,
      recurrenceText: formatRecurrenceRuleText(slotRule, timezoneName),
    }];
  }).sort((a, b) => a.start.localeCompare(b.start));

  if (!selectableSlots.length) return null;

  return {
    selectableSlots,
    visibleDates: [],
  };
};

interface ComponentProps {
  externalId?: string,
  isPreview?: boolean,
  prefillName?: string;
  prefillEmail?: string;
  isFramed?: boolean;
  noCabPage?: boolean;
  isEnterpriseBook?: boolean;
  setBookingComplete?: (data?: {isIntroProcessComplete: boolean, shouldUpdateOrg: boolean}) => void;
}

type RouteParams = { meetingId: string, meetingToken: string };

type Props = ComponentProps;

const BookMeetingContainer = ({
  externalId, isPreview, prefillEmail, prefillName, setBookingComplete, isFramed, noCabPage, isEnterpriseBook
}: Props): ReactElement => {
  const match = useParams<RouteParams>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const dispatch = useDispatch<ThunkDispatchType>();
  const location = useLocation();

  scheduling.useAppSchedulingPreferencesListQuery(isAuthenticated ? undefined : skipToken);

  const { start: urlStart, end: urlEnd, framed } = queryString.parse(location.search, { parseBooleans: true }) as {
    start?: string; end?: string; framed?: boolean;
  };
  
  const { meetingId } = match;
  const meetingExternalId = externalId || meetingId;
  const meetingInfo = useSelector((state: RootState) => meetingExternalId
    ? state.schedule.publicMeetings[meetingExternalId]
    : null);
  const curAnswers = useSelector((state: RootState) => state.schedule.questionAnswers);

  const [currentTimeZone, setCurrentTimeZone] = useState<string>(getLocalTimeZoneName());

  // make sure we validate url parameters
  const urlStartDateTime = useMemo(() => {
    let start = urlStart ? DateTime.fromISO(urlStart, { zone: currentTimeZone }) : undefined;
    if (start && !start.isValid) {
      start = undefined;
    }
    return start;
  }, [urlStart, currentTimeZone]);

  const urlEndDateTime = useMemo(() => {
    let end = urlEnd ? DateTime.fromISO(urlEnd, { zone: currentTimeZone }) : undefined;
    if (end && !end.isValid) {
      end = undefined;
    }
    return end;
  }, [urlEnd, currentTimeZone]);

  const [step, setStep] = useState<MeetingBookingStep>(MeetingBookingStep.DATE_SELECTION);
  const [selectedDay, setSelectedDay] = useState(urlStartDateTime || null);
  const [slotSelected, setSlotSelected] = useState<BookingSlot | null>(null);
  const [slotsByDay, setSlotsByDay] = useState<BookingSlotsByDay>({});
  const [recurringVisibleDates, setRecurringVisibleDates] = useState<DateTime[]>([]);
  const [isBooking, setIsBooking] = useState(false);
  const [allowAddParticipants, setAllowAddParticipants] = useState(true);
  const [confirmed, setConfirmed] = useState(false);
  const [isBadResponse, setIsBadResponse] = useState(false);
  const [isSelectedDay, setIsSelectedDay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [defaultYearMonth, setDefaultYearMonth] = useState<[number, number] | null>(null);
  const [rebookParticipants, setRebookParticipants] = useState<NormalizedExternalParticipant[]>([]);
  const [rebookAnswers, setRebookAnswers] = useState<MeetingQuestionAnswerSubmission[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [viewDate, setViewDate] = useState<DateTime>(DateTime.now());
  const [noTimesWorkSent, setNoTimesWorkSent] = useState(false);
  const [loadingAnswers, setLoadingAnswers] = useState(true);

  const { data: organization } = core.useCoreOrgPublicOrgByUserRetrieveQuery(
    meetingInfo?.create_user.id ? meetingInfo.create_user.id.toString() : skipToken
  );

  const navigate = useNavigate();

  const isReschedulingURL = location.pathname.includes("reschedule");
  const orderedAvailableDates = useMemo(() => (
    Object.keys((slotsByDay))
      .map(s => Number(s))
      .sort(orderTimeSlots)
      .map(s => DateTime.fromMillis(s, { zone: currentTimeZone }))
  ), [slotsByDay, currentTimeZone]);

  const selectedRecurringSeriesDates = useMemo(() => (
    ((slotSelected as RecurringBookingSlot | null)?.recurrenceSeriesDates || [])
      .map(date => DateTime.fromISO(date, { zone: currentTimeZone }))
      .filter(date => date.isValid)
  ), [currentTimeZone, slotSelected]);

  useEffect(() => {
    if (meetingInfo && meetingInfo.is_poll) {
      const path = location.pathname.replace("book", "poll");
      navigate(path);
    }
  }, [navigate, meetingInfo, location.pathname]);

  useEffect(() => {
    if (noTimesWorkSent && setBookingComplete) {
      setBookingComplete({isIntroProcessComplete: true, shouldUpdateOrg: !noTimesWorkSent});
    }
  }, [noTimesWorkSent, setBookingComplete]);

  const onCalendarViewChange = useCallback(async (
    date: DateTime, timezone?: string
  ): Promise<[
    ExternalMeetingInfo | Record<never, never>, DateTime | null, BookingSlot[], { start: DateTime; end: DateTime }
  ] | undefined> => {
    const tz = timezone || currentTimeZone;
    const curDate = date.setZone(tz);
    setViewDate(curDate);
    const slotsStart = curDate.set({ day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 });
    const slotsEnd = slotsStart.endOf('month');

    const { meetingToken } = match;
    if (!meetingExternalId) return;

    setLoadingCalendar(true);

    const res = await dispatch(actions.schedule.fetchMeetingExternal(
      meetingExternalId, meetingToken, slotsStart, slotsEnd,
    ));

    let firstAvailableDate: DateTime | null = null;
    let bookingSlots: BookingSlot[] = [];

    if ('status' in res) {
      // verify slots really are in the requested time interval
      bookingSlots = res.booking_slots
        .map(s => ({
          ...s,
          start: DateTime.fromISO(s.start).setZone(tz).toISO() || "",
          end: DateTime.fromISO(s.end).setZone(tz).toISO() || "",
        }))
        .filter(s => (
          DateTime.fromISO(s.start).toMillis() >= slotsStart.toMillis()
          && DateTime.fromISO(s.start).toMillis() <= slotsEnd.toMillis()
        ));

      const recurrenceView = getRecurringBookingView(res, bookingSlots, tz);
      const slotsForSelection = recurrenceView?.selectableSlots || bookingSlots;
      const curSlotsByDay = timeSlotsByDay(bookingSlots, tz);
      const displaySlotsByDay = recurrenceView ? timeSlotsByDay(recurrenceView.selectableSlots, tz) : curSlotsByDay;

      firstAvailableDate = Object.keys((displaySlotsByDay))
        .map(s => Number(s))
        .sort()
        .map(s => DateTime.fromMillis(s, { zone: tz }))
        .at(0) || null;

      let newSelectedDay: DateTime | null = null;

      if (slotsForSelection.find(s => DateTime.fromISO(s.start).toMillis() === curDate.toMillis())) {
        newSelectedDay = curDate;
      } else {
        newSelectedDay = firstAvailableDate;
      }

      // this is for mobile
      setIsSelectedDay(true);

      setSlotsByDay(displaySlotsByDay);
      setRecurringVisibleDates(recurrenceView?.visibleDates || []);
      setSelectedDay(newSelectedDay || date);
    }

    setLoadingCalendar(false);

    return [res, firstAvailableDate, bookingSlots, { start: slotsStart, end: slotsEnd }];
  }, [dispatch, match, currentTimeZone, meetingExternalId]);

  const handleLoadInitialTimes = useCallback(async () => {
    setLoading(true);
    const date = urlStartDateTime || DateTime.now();

    const onChangeInitialRes = await onCalendarViewChange(date);
    if (onChangeInitialRes) {
      let [res, firstAvailableDate, slots, range] = onChangeInitialRes;
      if (!('status' in res)) {
        setLoading(false);
        return;
      }

      const scheduledDay = res.status.id === MeetingStatus.PENDING && res.event_start_time
        ? DateTime.fromISO(res.event_start_time)
        : null;

      // need to check whether scheduledDay is actually in the currently fetched dates. If not, fetch again.
      if (scheduledDay && scheduledDay > DateTime.now() && (scheduledDay < range.start || scheduledDay > range.end)) {
        const onChangeResScheduleDayOutofRange = await onCalendarViewChange(scheduledDay);
        if (onChangeResScheduleDayOutofRange) {
          [res, firstAvailableDate, slots, range] = onChangeResScheduleDayOutofRange;
        }
      } else if (!firstAvailableDate) {
        let earliestDate: DateTime | undefined = undefined;
        if (('status' in res)) {
          earliestDate = res.earliest_slot_date ? DateTime.fromISO(res.earliest_slot_date) : undefined;
        }
        const nextMonthDate = earliestDate && earliestDate > date.plus({ month: 1 }) 
          ? earliestDate : date.plus({ month: 1 });
        const onChangeResFalseyFirstAvailableDate = await onCalendarViewChange(nextMonthDate);
        if (onChangeResFalseyFirstAvailableDate) {
          [res, firstAvailableDate, slots, range] = onChangeResFalseyFirstAvailableDate;
        }

        // if we still don't find anything in the next month, reset the selected day
        if (!firstAvailableDate) {
          setSelectedDay(date);
        }
      }

      if (firstAvailableDate && date && slots?.length) {
        const autoSelectSlot = slots.find(s => (
          DateTime.fromISO(s.start).toMillis() === date.toMillis()
          && (!urlEndDateTime || DateTime.fromISO(s.end).toMillis() === urlEndDateTime.toMillis())
        ));
        if (autoSelectSlot) {
          setSlotSelected(autoSelectSlot);
        }
      }
      const calViewDate = firstAvailableDate || date;

      if (!('status' in res)) {
        setLoading(false);
        return;
      }

      if (isMobile() && !slotSelected) {
        setIsSelectedDay(false);
      }

      setDefaultYearMonth([calViewDate.year, calViewDate.month]);
      setViewDate(calViewDate);
      setShowConflictModal(false);
      setAllowAddParticipants(res.allow_add_participants !== undefined ? res.allow_add_participants : true);

      if (res.status.id !== 1 || !res.use_link) {
        if (isReschedulingURL) {
          setStep(MeetingBookingStep.DATE_SELECTION);
        } else {
          setStep(MeetingBookingStep.MEETING_CONFIRMATION);
        }
      } else {
        setStep(MeetingBookingStep.DATE_SELECTION);
      }
    }

    setLoading(false);

  }, [onCalendarViewChange, urlStartDateTime, urlEndDateTime, isReschedulingURL, slotSelected]);
  
  const prevStep = step === MeetingBookingStep.MEETING_INFO_STEP || step === MeetingBookingStep.NO_TIMES_WORK
    ? MeetingBookingStep.DATE_SELECTION : null;
    
  const onBackButtonClick = useMemo(() => prevStep != null ? () => setStep(prevStep) : undefined, [prevStep, setStep]);

  // find the first available slot (if any) so we can set the default calendar view to that month
  useMountEffect(() => {
    handleLoadInitialTimes();
  });

  useEffect(() => {
    if (meetingInfo?.questions?.length) {
      setLoadingAnswers(true);
      dispatch(actions.schedule.fetchExternalMeetingAnswers(meetingInfo.external_id)).finally(() => {
        setLoadingAnswers(false);
      });
    }
  }, [meetingInfo?.questions, meetingInfo?.external_id, dispatch]);
  
  const questionAnswers = useMemo(() => {
    const orderedQuestionAnswers: MeetingQuestionAnswerSubmission[] = [];
    if (curAnswers) {
      meetingInfo?.questions?.forEach((question) => {
        const answer = Object.values(curAnswers).find(curAnswer => curAnswer.question === question.id);
        if (answer) {
          orderedQuestionAnswers.push({ ...answer, participant: undefined });
        }
      });
    }
    return orderedQuestionAnswers;
  }, [curAnswers, meetingInfo?.questions]);
  
  const timeSlotsByDay = (slots: BookingSlot[], timezoneName: string) => {
    const eventsByDay: {
      [date: number]: Array<BookingSlot>
    } = {};

    slots.forEach(slot => {
      const day = DateTime.fromISO(slot.start).setZone(timezoneName).startOf('day').toMillis();
      eventsByDay[day] = eventsByDay[day] ? [...eventsByDay[day], slot] : [slot];
    });
    return eventsByDay;
  };

  const handleScheduleMeeting = (
    externalParticipants: NormalizedExternalParticipant[],
    meetingQuestionAnswers?: (Omit<MeetingQuestionAnswerSubmission, 'id'> & { id?: number })[],
  ) => {
    if (isPreview) return;
    
    const meetingToken = match.meetingToken;
    
    const answers = meetingQuestionAnswers?.map((answer, idx) => ({
      ...answer,
      text: answer.text || '',
      options: answer.options || [],
      id: answer.id || -idx
    }));

    //If there is no meeting token the component is being rendered in preview mode 
    //Therefore the request shouldn't be made
    if (meetingExternalId && slotSelected) {
      setIsBooking(true);
      const bookingResponse = (res: FetchReturn) => {
        if (res.status === 400 && res.data.code === 'slot_not_available') {
          setIsBooking(false);
          setRebookParticipants(externalParticipants);
          setRebookAnswers(answers || []);
          setShowConflictModal(true);
        } else if (res.status !== 200) {
          setIsBadResponse(true);
          setStep(MeetingBookingStep.MEETING_CONFIRMATION);
        } else {
          setConfirmed(true);
          if (setBookingComplete) {
            setBookingComplete({isIntroProcessComplete: true, shouldUpdateOrg: !noTimesWorkSent});
          }
          setIsBooking(false);
          setStep(MeetingBookingStep.MEETING_CONFIRMATION);
          if (res.data.event_id) {
            successConfetti();
            // when a reusable meeting is booked, the external_id will be different for the resulting meeting,
            // so we update the url accordingly
            if (res.data.external_id && res.data.external_id !== meetingExternalId) {
              const newPath = location.pathname.replace(meetingExternalId, res.data.external_id);
              navigate(newPath, { replace: true });
            }
          }
          trackEvent(EVENT_TYPE.MEETING_BOOKED);
        }
      };

      if (isReschedulingURL && meetingToken) {
        dispatch(actions.schedule.bookExternalReschedule(
          meetingExternalId,
          meetingToken,
          externalParticipants,
          slotSelected.start,
          answers
        )).then(bookingResponse);
      } else {
        if (meetingInfo && organization) {
          dispatch(actions.schedule.bookExternal(
            meetingExternalId,
            externalParticipants,
            slotSelected.start,
            organization.id,
            meetingInfo?.create_user.id,
            organization.name,
            answers
          )).then(bookingResponse);
        }
      }
    } else {
      setStep(MeetingBookingStep.MEETING_CONFIRMATION);
    }
  };

  const handleUpdateTimezone = (tz: string) => {
    setCurrentTimeZone(tz);
    setSlotSelected(null);
    onCalendarViewChange(viewDate.setZone(tz), tz);
    // setSelectedDay(null);
  };

  const handleDaySelectionChange = useCallback((daySelected: DateTime | null) => {
    setSelectedDay(daySelected);
    setSlotSelected(null);
  }, []);

  const submitNoTimesWorkForm = async (value: NormalizedExternalParticipant) => {
    const res = await dispatch(actions.schedule.sendNoTimesWork(value));
    if (res) {
      setNoTimesWorkSent(true);
    } else {
      setIsBadResponse(true);
    }
    setStep(MeetingBookingStep.MEETING_CONFIRMATION);
  };

  return (
    <>
      <BookMeeting
        step={step}
        onBackButtonClick={onBackButtonClick}
        meetingInfo={meetingInfo}
        isAuthenticated={isAuthenticated}
        selectedDay={selectedDay}
        orderedAvailableDates={orderedAvailableDates}
        recurringVisibleDates={recurringVisibleDates}
        selectedRecurringSeriesDates={selectedRecurringSeriesDates}
        slotsByDay={slotsByDay}
        currentTimezone={currentTimeZone}
        slotSelected={slotSelected}
        isBooking={isBooking}
        setCurrentTimeZone={handleUpdateTimezone}
        handleDaySelected={handleDaySelectionChange}
        setSlotSelected={setSlotSelected}
        onConfirm={() => setStep(step + 1)}
        onNoTimesWork={() => setStep(MeetingBookingStep.NO_TIMES_WORK)}
        handleScheduleMeeting={handleScheduleMeeting}
        isReschedulingURL={isReschedulingURL}
        allowAddParticipants={allowAddParticipants}
        confirmed={confirmed}
        isPreview={isPreview}
        isBadResponse={isBadResponse}
        isSelectedDay={isSelectedDay}
        prefillEmail={prefillEmail}
        prefillName={prefillName}
        isFramed={framed ?? isFramed}
        onCalendarDateChange={onCalendarViewChange}
        loading={loading}
        defaultYearMonth={defaultYearMonth}
        rebookParticipants={rebookParticipants}
        rebookAnswers={rebookAnswers}
        viewDate={viewDate}
        noCabPage={noCabPage}
        isEnterpriseBook={isEnterpriseBook}
        submitNoTimesWorkForm={submitNoTimesWorkForm}
        noTimesWorkSent={noTimesWorkSent}
        loadingCalendar={loadingCalendar}
        loadingAnswers={loadingAnswers}
        questionAnswers={questionAnswers}
      />
      {showConflictModal && (
        <CabModal
          open={showConflictModal}
          onClose={() => setShowConflictModal(false)}
          title={`Time Not Available`}
          text="Sorry, the time you selected is no longer available. Please select a new time to continue."
          isAlert={true}
          noFullScreen={true}
          actionButtons={
            <>
              <CabButton buttonType='primary' color='primary' disabled={loading} onClick={handleLoadInitialTimes}>
                Select a new time
              </CabButton>
            </>
          }
        />
      )}
    </>
  );
};

export const Meeting = BookMeetingContainer;

export default BookMeetingContainer;
